/**
 * 「今日のこと」のカードの中身（ADR-0026 の 11）。画面に依存しない部分（何を出すか・どのボタンが何を送るか）。
 * 文面は Gateway がコードで作った定型文をそのまま出す（ここで言い換えない）。
 */

export interface BriefingItemView {
  id: string;
  /** self_failures: 昨日の自分の不調（ADR-0039。packages/contracts の BriefingSourceKind と同じ並び） */
  source_kind: "list_due" | "list_new" | "list_summary" | "fact_date" | "self_failures";
  text: string;
  status: "shown" | "done" | "dismissed";
  reaction: "useful" | "not_needed" | null;
  can_done: boolean;
  position: number;
}

export interface BriefingView {
  enabled: boolean;
  briefing_date: string | null;
  trace_id: string | null;
  items: BriefingItemView[];
  nightly_pending: number | null;
  /** 見張り役の警告の数（ADR-0052）。古い Gateway・読めないときは無し / null */
  monitor_alerts?: { count: number; hours: number } | null;
  frozen: boolean;
}

export type BriefingBody =
  | { reaction: "useful" | "not_needed" }
  | { action: "done" | "dismiss" | "undismiss" };

export interface ButtonSpec {
  label: string;
  body: BriefingBody;
  /** 押してある反応（見た目で分かるように） */
  pressed?: boolean;
  primary?: boolean;
}

export interface CardModel {
  /** false ならカードを出さない */
  show: boolean;
  /** カードに並べる件（済んだものも、済みと分かる形で残す） */
  active: BriefingItemView[];
  /** 「もういい」で閉じたもの（開くと戻せる） */
  closed: BriefingItemView[];
  /** 「昨夜の整理: 確認が N 件あります」（件数に数えない案内の行）。無ければ null */
  nightlyLine: string | null;
  /** 「見張り役: この 24 時間に警告が N 件あります」（件数に数えない案内の行）。無ければ null */
  monitorLine: string | null;
}

export function cardModel(view: BriefingView): CardModel {
  const nightlyLine =
    view.nightly_pending && view.nightly_pending > 0
      ? `昨夜の整理: 確認が ${view.nightly_pending} 件あります（下の「昨夜の整理」）`
      : null;
  const monitorLine =
    view.monitor_alerts && view.monitor_alerts.count > 0
      ? `見張り役: この ${view.monitor_alerts.hours} 時間に警告が ${view.monitor_alerts.count} 件あります（上の「見張り役」）`
      : null;
  const sorted = [...view.items].sort((a, b) => a.position - b.position);
  const active = sorted.filter((i) => i.status !== "dismissed");
  const closed = sorted.filter((i) => i.status === "dismissed");
  const show =
    view.enabled &&
    !view.frozen &&
    (sorted.length > 0 || nightlyLine !== null || monitorLine !== null);
  return { show, active, closed, nightlyLine, monitorLine };
}

/** 1 件のボタン。役に立った／不要（計測）、済んだ（リストの項目だけ）、もういい */
export function itemButtons(item: BriefingItemView): ButtonSpec[] {
  if (item.status === "dismissed") return [{ label: "戻す", body: { action: "undismiss" } }];
  const out: ButtonSpec[] = [
    {
      label: "役に立った",
      body: { reaction: "useful" },
      pressed: item.reaction === "useful",
    },
    {
      label: "不要",
      body: { reaction: "not_needed" },
      pressed: item.reaction === "not_needed",
    },
  ];
  if (item.status === "shown") {
    if (item.can_done) out.push({ label: "済んだ", body: { action: "done" }, primary: true });
    out.push({ label: "もういい", body: { action: "dismiss" } });
  }
  return out;
}

/** ボタンを押す（1 回だけ API を呼ぶ）。呼び出しは差し替えられる（テスト用） */
export async function press(
  post: (path: string, body: BriefingBody) => Promise<unknown>,
  item: BriefingItemView,
  body: BriefingBody,
): Promise<void> {
  await post(`/v1/briefing/items/${encodeURIComponent(item.id)}`, body);
}
